import React from "react";
import Media from "@onnit-js/ui/components/media";
import styled, { ThemeProvider } from "styled-components";
import GlobalStyle from "@onnit-js/ui/components/themes/GlobalStyle";
import theme from "@onnit-js/ui/components/themes/light";
import Box from "@onnit-js/ui/components/box/Box";
import { Text } from "@onnit-js/ui/components/text";
import useOnnitContext from "@onnit-js/ui/hooks/useOnnitContext";
import { Img, Picture } from "@onnit-js/ui/components/image";
import ProductImageGrid from "@onnit-js/ui/components/module/ProductImageGrid";
import MediaHero01 from "@onnit-js/ui/components/module/MediaHero01";
import MediaShowcase01 from "@onnit-js/ui/components/module/MediaShowcase01";
import CopyDisplay02 from "@onnit-js/ui/components/module/CopyDisplay02";
import ImageContentPortrait from "@onnit-js/ui/components/module/ImageContentPortrait";
import MediaCollage03 from "@onnit-js/ui/components/module/MediaCollage03";
import Embed from "@onnit-js/ui/components/video/Embed";
import Video from "@onnit-js/ui/components/video/Video";
import LeftLineCTA from "@onnit-js/ui/components/cta/LeftLineCTA";
import Dash from "@onnit-js/ui/components/icon/Dash";
import { FaFileDownload } from "react-icons/fa";
import Icon from "@onnit-js/ui/components/icon/Icon";
import TwelveColumn from "@onnit-js/ui/components/module/grid/12/Grid";
import usePageData from "../../hooks/usePageData";
import config from "../../config/config";
import DefaultProductPageTop from "../../components/common/layouts/DefaultProductPageTop";

const WaterContainer = styled.div`
    position: relative;
    &:before {
        content: " ";
        position: absolute;
        z-index: 2;
        background-color: #016ce6;
        width: 100%;
        height: 100%;
        mix-blend-mode: color;
    }
`;

export default function Page() {
    const onnitContext = useOnnitContext();
    const { pageData } = usePageData();

    if (!pageData || !onnitContext) {
        return null;
    }

    const IMGIX_URL = `${onnitContext.url.cdn.static_images}/product-page/hydrocore`;
    const SCDN_URL = `${onnitContext.url.cdn.static}/images/product-page/hydrocore`;
    const { IMG_PARAMS } = config;

    const blue = "#006DE5";
    const purple = "#D6535C";

    return (
        <ThemeProvider theme={theme}>
            <>
                <GlobalStyle />

                <DefaultProductPageTop
                    buyAreaChild={(
                        <Box display="flex" alignItems="center" py={3} my={6}>
                            <a
                                href={`${SCDN_URL}/Hydrocore_Manual.pdf`}
                                target="_blank"
                                style={{ display: "flex", alignItems: "center" }}
                                rel="noreferrer"
                            >
                                <Icon icon={FaFileDownload} size={28} mr={3} color="grays.3" />
                                <Text textDecoration="underline">HydroCore Manual Download</Text>
                            </a>
                        </Box>
                    )}
                >
                    <ProductImageGrid>
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={494}
                            alt="Hydrocore Bag close up shot on logo"
                            src={[
                                `${IMGIX_URL}/hydro1-mobile.jpg${IMG_PARAMS}`,
                                `${IMGIX_URL}/hydro1.jpg${IMG_PARAMS}`,
                            ]}
                        />
                        <Picture
                            lazyLoad={false}
                            width={460}
                            height={460}
                            alt="Hydrocore Bag in the grass"
                            src={[`${IMGIX_URL}/hydro2.jpg${IMG_PARAMS}`]}
                        />
                        <Picture
                            lazyLoad={false}
                            width={460}
                            height={460}
                            alt="Woman using Hydrocore Bag for ab exercise"
                            src={[`${IMGIX_URL}/hydro3.jpg${IMG_PARAMS}`]}
                        />
                        <Picture
                            lazyLoad={false}
                            width={944}
                            height={494}
                            alt="Hydrocore Bag in the pool"
                            src={[
                                `${IMGIX_URL}/hydro4-mobile.jpg${IMG_PARAMS}`,
                                `${IMGIX_URL}/hydro4.jpg${IMG_PARAMS}`,
                            ]}
                        />
                    </ProductImageGrid>
                </DefaultProductPageTop>

                <div className="hide-ie">
                    <Box bg="grays.8" mt={8} py={[6, 6, 6, 8]}>
                        <MediaHero01
                            media={(
                                <Img
                                    alt="Man using the hydrocore bag"
                                    src={`${IMGIX_URL}/hydro5.jpg${IMG_PARAMS}`}
                                    width={1168}
                                    height={1168}
                                />
                            )}
                            headline={(
                                <>
                                    <Text typeStyle="headlineHero01" color={purple}>
                                        Hydro
                                    </Text>
                                    <Text typeStyle="headlineHero01" color={purple}>
                                        Core
                                    </Text>
                                    <Text typeStyle="headlineHero01" color={purple}>
                                        Bag
                                    </Text>
                                </>
                            )}
                        />

                        <CopyDisplay02 py={8} dashColor={blue} mb={[0, 0, 0, 8]}>
                            <Text typeStyle="copyDisplay02" color="white" mb={[6, 6, 6, 8]}>
                                “Water can flow or it can crash. Be water, my friend.”{" "}
                                <Text
                                    display="inline"
                                    typeStyle="copy03"
                                    color="white"
                                    style={{ whiteSpace: "nowrap" }}
                                >
                                    – Bruce Lee
                                </Text>
                            </Text>

                            <Text as="p" typeStyle="copyHero02" color="white">
                                The HydroCore bag uses the power of water dynamics to build stability, strength, and
                                explosiveness like no other training tool. Fold it up and take it on the road for a
                                workout you can do anytime, anywhere—no heavy weights necessary.
                            </Text>
                        </CopyDisplay02>

                        <TwelveColumn>
                            <Box gridColumn={["1 / -1", "1 / -1", "1 / -1", "2 / span 10"]} px={4}>
                                <Embed
                                    title="Hydrocore Bag Product Video"
                                    src="https://player.vimeo.com/video/365861993?title=0&byline=0&portrait=0"
                                />
                            </Box>
                        </TwelveColumn>
                    </Box>

                    <ImageContentPortrait
                        py={[6, 6, 6, 8]}
                        image={(
                            <Img
                                alt="Man using the hydrocore bag"
                                src={`${IMGIX_URL}/hydro6.jpg${IMG_PARAMS}`}
                                width={928}
                                height={1200}
                            />
                        )}
                        content={(
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text typeStyle="copyDisplay02" mb={[4, 4, 6]}>
                                    The Power of <br />
                                    Water Dynamics
                                </Text>
                                <Text as="p" typeStyle="copyHero02">
                                    When you lift the HydroCore bag, it wants to keep going in whatever direction you
                                    were moving it. The water inside it sloshes around, and your shoulders, hands, hips,
                                    and core have to work to stop it from pulling you out of position and breaking form.
                                </Text>
                            </Box>
                        )}
                    />

                    <ImageContentPortrait
                        reverse
                        py={[6, 6, 6, 8]}
                        image={(
                            <Img
                                alt="Man using the hydrocore bag"
                                src={`${IMGIX_URL}/hydro7.jpg${IMG_PARAMS}`}
                                width={928}
                                height={1200}
                            />
                        )}
                        content={(
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text as="p" typeStyle="copyHero02" mb={4}>
                                    Overcoming all that inertia builds head-to-toe stability—the kind you need to hold
                                    onto a resisting opponent if you’re a fighter, lift heavier weights if you’re a
                                    gym rat, and strengthen your lower back if you sit in a chair all day.
                                </Text>
                                <Text as="p" typeStyle="copyHero02">
                                    HydroCore training also teaches you good technique just by using it. The water’s
                                    momentum will help you learn the right speed and timing with which to perform your
                                    exercises. If the water sloshes a lot, you know something’s off. If it flows with
                                    you, you’re doing it right.
                                </Text>
                            </Box>
                        )}
                    />

                    <Media query="(min-width: 768px)">
                        {(matches) => (matches ? (
                            <Video
                                autoPlay
                                loop
                                muted
                                playsInline
                                placeholder={[1570, 883]}
                                style={{ position: "relative", zIndex: 1, width: "100%" }}
                            >
                                <source src={`${SCDN_URL}/loopWithText.mp4`} type="video/mp4" />
                            </Video>
                        ) : (
                            <Box px={5} pb={7}>
                                <Img
                                    alt="People working out the hydrocore bag"
                                    src={`${SCDN_URL}/hydrotype2.gif`}
                                    style={{ maxWidth: "1500px", margin: "auto", width: "80%" }}
                                    width={750}
                                    height={422}
                                />
                            </Box>
                        ))}
                    </Media>

                    <ImageContentPortrait
                        pt={[6, 6, 6, 8]}
                        pb={[0, 6, 6, 8]}
                        image={(
                            <Img
                                alt="Man using the hydrocore bag"
                                src={`${IMGIX_URL}/hydro8.png${IMG_PARAMS}`}
                                width={1146}
                                height={1668}
                            />
                        )}
                        content={(
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text typeStyle="copyDisplay02" mb={6}>
                                    Squat it. Row it. Press it. Swing it. Carry it. Throw it across your backyard.
                                </Text>
                            </Box>
                        )}
                    />
                    <ImageContentPortrait
                        reverse
                        py={[6, 6, 6, 8]}
                        image={(
                            <Img
                                alt="Man using the hydrocore bag"
                                src={`${IMGIX_URL}/hydro9.jpg${IMG_PARAMS}`}
                                width={928}
                                height={1200}
                            />
                        )}
                        content={(
                            <Box display="flex" flexDirection="column" height="100%" justifyContent="center">
                                <Text typeStyle="copyDisplay02" mb={6}>
                                    The HydroCore bag trains your whole body. Just add water.
                                </Text>
                                <LeftLineCTA
                                    href="http://www.onnit.com/academy/hydrocore-bag-training-for-an-unshakeable-body"
                                >
                                    Read: HydroCore Bag Training
                                </LeftLineCTA>
                            </Box>
                        )}
                    />

                    <Box bg="grays.8" py={[6, 6, 8]}>
                        <CopyDisplay02 dashColor={blue} mb={[0, 0, 0, 8]}>
                            <Text typeStyle="copyDisplay02" color="white" mb={6}>
                                Tough Enough for Navy SEALs
                            </Text>

                            <Text as="p" typeStyle="copyHero02" color="grays.4">
                                Invented by Maurizio Tangari, trainer to MMA fighters, military personnel, and Hollywood
                                celebrities, the HydroCore Bag design was inspired by Zodiac inflatable boats—the kind
                                that Navy SEALs use on top secret missions. This bag won’t spring a leak or lose its
                                shape, even under the heavy fire of your hardest workouts.
                            </Text>
                        </CopyDisplay02>
                        <MediaCollage03
                            item1={(
                                <Media query="(min-width: 768px)">
                                    {(matches) => (matches ? (
                                        <Video
                                            autoPlay
                                            loop
                                            muted
                                            playsInline
                                            placeholder={[1570, 883]}
                                            style={{
                                                position: "relative",
                                                zIndex: 1,
                                                width: "100%",
                                            }}
                                            poster={`${IMGIX_URL}/boat.png${IMG_PARAMS}`}
                                        >
                                            <source src={`${SCDN_URL}/boat.mp4`} type="video/mp4" />
                                        </Video>
                                    ) : (
                                        <Img
                                            alt="Navy seals in a boat"
                                            src={`${IMGIX_URL}/boat.png${IMG_PARAMS}`}
                                            width={580}
                                            height={324}
                                        />
                                    ))}
                                </Media>
                            )}
                            item2={(
                                <Img
                                    alt="Navy seals in a boat"
                                    src={`${IMGIX_URL}/gallery1.jpg${IMG_PARAMS}`}
                                    width={448}
                                    height={448}
                                />
                            )}
                            item3={(
                                <Img
                                    alt="Navy seal diver"
                                    src={`${IMGIX_URL}/gallery2.jpg${IMG_PARAMS}`}
                                    width={1170}
                                    height={656}
                                />
                            )}
                        />
                    </Box>

                    <TwelveColumn>
                        <Box gridColumn={["1 / -1", "1 / -1", "2 / span 10"]} my={[8, 8, 10]}>
                            <MediaShowcase01
                                overlayImage={(
                                    <Img
                                        alt="Hydrocore bag product image"
                                        src={`${IMGIX_URL}/hydro10.png${IMG_PARAMS}`}
                                        width={1126}
                                        height={1516}
                                    />
                                )}
                            >
                                <WaterContainer id="water">
                                    <Video
                                        autoPlay
                                        loop
                                        muted
                                        playsInline
                                        placeholder={[1570, 883]}
                                        style={{ position: "relative", zIndex: 1, width: "100%" }}
                                    >
                                        <source src={`${SCDN_URL}/waterBg.mp4`} type="video/mp4" />
                                    </Video>
                                </WaterContainer>
                            </MediaShowcase01>
                        </Box>
                    </TwelveColumn>

                    <TwelveColumn>
                        <Box gridColumn={["1 / -1", "1 / -1", "2 / span 9"]} px={[5, 5, 0]}>
                            <Dash color={blue} mb={4} />
                            <Text typeStyle="copyDisplay02" mb={[6, 6, 8]}>
                                HERE'S WHAT SETS THE HYDROCORE BAG APART FROM ITS COMPETITORS
                            </Text>
                            <Text typeStyle="copy01" color={blue} mb={2}>
                                Ball stoppers on the handles
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={6}>
                                Ensuring a no-slip grip.
                            </Text>

                            <Text typeStyle="copy01" color={blue} mb={2}>
                                One-way valve
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={6}>
                                Water goes in and stays in, no matter how fast the bag moves or how hard it lands.
                            </Text>

                            <Text typeStyle="copy01" color={blue} mb={2}>
                                Light weight
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={6}>
                                The empty bag fits easily into luggage, and 10 pounds of water is all it needs to
                                provide a great workout for almost anybody. Less is more.
                            </Text>
                            <Text typeStyle="copy01" color={blue} mb={2}>
                                Fun!
                            </Text>
                            <Text as="p" typeStyle="copyHero02" color="grays.4" mb={6}>
                                HydroCore bag training makes hard work feel like play. Make up your own workouts and
                                take your training outside, on the beach, or anywhere you enjoy.
                            </Text>
                        </Box>
                    </TwelveColumn>
                </div>
            </>
        </ThemeProvider>
    );
}
